// ============================================================================
// PlayerStatsPage - the player's performance hub
// ============================================================================
// Play-quality stats (hands, VPIP, showdown win - currency-independent)
// plus MONEY figures strictly separated by currency. PlayCoins and PSS
// Coins are different economies, so the chart, weekly net, biggest win and
// recent results all switch with the selected currency - never mixed.
// Home game ledgers live on their own page (/home-ledgers) - money
// records, not player stats.
// The 7-day chart uses signed bars around a zero baseline so polarity reads
// by POSITION, with the app's up/down colors as reinforcement.
// ============================================================================
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Spinner, Button,
} from '@chakra-ui/react';
import { gameApiService } from '../services/gameApiService';

type Cur = 'playcoins' | 'pkcash';
interface DayNet { label: string; net: number }
interface RecentRow { handNumber: number | null; amount: number; ts: number }
interface CurBucket { days: DayNet[]; recent: RecentRow[]; biggestWin: number; weekNet: number }

const CARD_BG = '#0f1722';
const CARD_BORDER = 'rgba(0,217,217,0.25)';
const UP = '#4ade80';
const DOWN = '#f87171';

const fmtAmount = (n: number, cur: Cur) => cur === 'pkcash'
  ? `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : `${Math.abs(Math.round(n)).toLocaleString('en-US')} PC`;

const timeAgo = (ts: number) => {
  const m = Math.max(1, Math.round((Date.now() - ts) / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const StatTile = ({ label, value, accent }: { label: string; value: string; accent?: string }) => (
  <Box bg={CARD_BG} border="1px solid" borderColor={CARD_BORDER} borderRadius="xl" p={4} textAlign="center">
    <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="wider">{label}</Text>
    <Text fontSize="2xl" fontWeight="900" color={accent || 'white'} mt={1}>{value}</Text>
  </Box>
);

const Segment = ({ options, value, onChange }: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (k: string) => void;
}) => (
  <HStack bg="#0b1320" border="1px solid rgba(148,163,184,0.2)" borderRadius="full" p={1} spacing={1}>
    {options.map(o => (
      <Button
        key={o.key}
        size="sm"
        flex={1}
        borderRadius="full"
        fontWeight="800"
        bg={value === o.key ? 'rgba(0,217,217,0.18)' : 'transparent'}
        color={value === o.key ? '#00d9d9' : 'gray.500'}
        border={value === o.key ? '1px solid rgba(0,217,217,0.5)' : '1px solid transparent'}
        _hover={{ color: '#00d9d9' }}
        onClick={() => onChange(o.key)}
      >
        {o.label}
      </Button>
    ))}
  </HStack>
);

const WeekChart = ({ days, currency, weekNet }: { days: DayNet[]; currency: Cur; weekNet: number }) => {
  const W = 340, H = 170, PAD = 8, LABEL_H = 22;
  const plotH = H - LABEL_H - PAD;
  const zeroY = PAD + plotH / 2;
  const maxAbs = Math.max(1, ...days.map(d => Math.abs(d.net)));
  const barW = Math.floor((W - PAD * 2) / Math.max(1, days.length)) - 10;

  return (
    <Box bg={CARD_BG} border="1px solid" borderColor={CARD_BORDER} borderRadius="xl" p={4}>
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" fontWeight="700" color="gray.300">Last 7 days</Text>
        <Text fontSize="sm" fontWeight="900" color={weekNet > 0 ? UP : weekNet < 0 ? DOWN : 'gray.400'}>
          {weekNet > 0 ? '+' : weekNet < 0 ? '-' : ''}{fmtAmount(weekNet, currency)}
        </Text>
      </HStack>
      <Box as="svg" width="100%" viewBox={`0 0 ${W} ${H}`} display="block">
        <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} stroke="rgba(148,163,184,0.35)" strokeWidth="1" />
        {days.map((d, i) => {
          const x = PAD + i * ((W - PAD * 2) / days.length) + 5;
          const h = Math.abs(d.net) / maxAbs * (plotH / 2 - 4);
          const isUp = d.net > 0;
          const y = isUp ? zeroY - h : zeroY;
          return (
            <g key={i}>
              <rect
                x={x} y={y} width={barW} height={Math.max(d.net === 0 ? 0 : 3, h)}
                rx={3}
                fill={d.net === 0 ? 'rgba(148,163,184,0.25)' : isUp ? UP : DOWN}
              >
                <title>{`${d.label}: ${d.net > 0 ? '+' : d.net < 0 ? '-' : ''}${fmtAmount(d.net, currency)}`}</title>
              </rect>
              <text x={x + barW / 2} y={H - 6} textAnchor="middle" fontSize="11" fontWeight="700"
                fill="rgba(148,163,184,0.8)">{d.label}</text>
            </g>
          );
        })}
      </Box>
    </Box>
  );
};

const PlayerStatsPage = () => {
  const navigate = useNavigate();
  const [cur, setCur] = useState<Cur>('playcoins');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [byCurrency, setByCurrency] = useState<Record<Cur, CurBucket> | null>(null);



  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await gameApiService.getMyStats();
      if (cancelled) return;
      if (r.success) {
        setStats(r.stats || null);
        setByCurrency((r as any).byCurrency || null);
      } else {
        setError(r.error || 'Could not load your stats');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);


  const bucket: CurBucket = byCurrency?.[cur]
    || { days: [], recent: [], biggestWin: 0, weekNet: 0 };

  return (
    <Box minH="100vh" bg="#0a1420" pb={12}>
      <Container maxW="container.md" pt={6}>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Heading size="lg" color="white">Player Stats</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>Your game, hand by hand</Text>
          </Box>
          <Button size="sm" variant="outline" colorScheme="cyan" onClick={() => navigate('/profile')}>
            Back
          </Button>
        </HStack>


        {(loading ? (
          <Box textAlign="center" py={20}><Spinner color="cyan.400" size="lg" /></Box>
        ) : error ? (
          <Box bg={CARD_BG} border="1px solid" borderColor="red.500" borderRadius="xl" p={6} textAlign="center">
            <Text color="red.300">{error}</Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={5}>
            {/* Play quality - independent of currency */}
            <SimpleGrid columns={3} spacing={4}>
              <StatTile label="HANDS" value={(stats?.handsPlayed || 0).toLocaleString('en-US')} />
              <StatTile label="VPIP" value={stats?.vpipPct !== null && stats?.vpipPct !== undefined ? `${stats.vpipPct}%` : '-'} accent="#00d9d9" />
              <StatTile label="SD WIN" value={stats?.showdownWinPct !== null && stats?.showdownWinPct !== undefined ? `${stats.showdownWinPct}%` : '-'} accent={UP} />
            </SimpleGrid>

            {/* Money - strictly per currency */}
            <Box>
              <Text fontSize="xs" fontWeight="800" color="gray.500" letterSpacing="wider" mb={2}>
                RESULTS BY CURRENCY
              </Text>
              <Segment
                options={[
                  { key: 'playcoins', label: 'PlayCoins' },
                  { key: 'pkcash', label: 'PSS Coins' },
                ]}
                value={cur}
                onChange={k => setCur(k as Cur)}
              />
            </Box>

            <SimpleGrid columns={2} spacing={4}>
              <StatTile label="BIGGEST WIN" value={bucket.biggestWin > 0 ? fmtAmount(bucket.biggestWin, cur) : '-'} accent="#fbbf24" />
              <StatTile
                label="THIS WEEK"
                value={`${bucket.weekNet > 0 ? '+' : bucket.weekNet < 0 ? '-' : ''}${fmtAmount(bucket.weekNet, cur)}`}
                accent={bucket.weekNet > 0 ? UP : bucket.weekNet < 0 ? DOWN : undefined}
              />
            </SimpleGrid>

            <WeekChart days={bucket.days} currency={cur} weekNet={bucket.weekNet} />

            <Box>
              <Text fontSize="xs" fontWeight="800" color="gray.500" letterSpacing="wider" mb={2}>
                RECENT {cur === 'pkcash' ? 'PSS COINS' : 'PLAYCOINS'} RESULTS
              </Text>
              <Box bg={CARD_BG} border="1px solid" borderColor={CARD_BORDER} borderRadius="xl" overflow="hidden">
                {bucket.recent.length === 0 ? (
                  <Text color="gray.500" fontSize="sm" p={5} textAlign="center">
                    No {cur === 'pkcash' ? 'PSS Coins' : 'PlayCoins'} hands yet
                  </Text>
                ) : bucket.recent.map((r, i) => (
                  <HStack key={i} justify="space-between" px={4} py={3}
                    borderBottom={i < bucket.recent.length - 1 ? '1px solid rgba(148,163,184,0.08)' : 'none'}>
                    <Box>
                      <Text color="gray.200" fontSize="sm" fontWeight="600">
                        {r.amount >= 0 ? 'Won' : 'Lost'} hand{r.handNumber ? ` #${r.handNumber}` : ''}
                      </Text>
                      <Text color="gray.600" fontSize="xs">{timeAgo(r.ts)}</Text>
                    </Box>
                    <Text fontWeight="900" fontSize="sm" color={r.amount >= 0 ? UP : DOWN}>
                      {r.amount >= 0 ? '+' : '-'}{fmtAmount(r.amount, cur)}
                    </Text>
                  </HStack>
                ))}
              </Box>
            </Box>

            <Text fontSize="xs" color="gray.600" textAlign="center">
              PlayCoins and PSS Coins are separate currencies and are never mixed.
              VPIP is how often you voluntarily put chips in preflop.
            </Text>
          </VStack>
        ))}


      </Container>
    </Box>
  );
};

export default PlayerStatsPage;
